import React, { useEffect, useState } from 'react';
import Input from '../../../../components/ui/Input';
import Icon from '../../../../components/AppIcon';

const PricingTab = ({ productData, onFieldChange, validationErrors }) => {
  const [calculations, setCalculations] = useState({
    mercadolivreClassic: { suggestedPrice: 0, profit: 0, fee: 0, fixedCost: 0, margin: 0 },
    mercadolivrePremium: { suggestedPrice: 0, profit: 0, fee: 0, fixedCost: 0, margin: 0 },
    shopee: { suggestedPrice: 0, profit: 0, totalFees: 0, margin: 0 }
  });

  useEffect(() => {
    calculateMarketplacePrices();
  }, [productData?.cost, productData?.price]);

  const calculateMarketplacePrices = () => {
    const cost = parseFloat(productData?.cost) || 0;
    const userPrice = parseFloat(productData?.price) || 0;
    
    if (cost === 0 || userPrice === 0) {
      setCalculations({
        mercadolivreClassic: { suggestedPrice: 0, profit: 0, fee: 0, fixedCost: 0, margin: 0 },
        mercadolivrePremium: { suggestedPrice: 0, profit: 0, fee: 0, fixedCost: 0, margin: 0 },
        shopee: { suggestedPrice: 0, profit: 0, totalFees: 0, margin: 0 }
      });
      return;
    }

    // Mercado Livre Classic (11.5% + R$6.25)
    const mlClassicFeePercent = 11.5;
    const mlClassicFixedCost = 6.25;
    // To achieve user's desired selling price after fees, calculate required listing price
    // userPrice = listingPrice - (listingPrice * fee%) - fixedCost
    // userPrice = listingPrice * (1 - fee%) - fixedCost
    // listingPrice = (userPrice + fixedCost) / (1 - fee%)
    const mlClassicListingPrice = (userPrice + mlClassicFixedCost) / (1 - mlClassicFeePercent / 100);
    const mlClassicFee = (mlClassicListingPrice * mlClassicFeePercent) / 100;
    const mlClassicProfit = userPrice - cost;
    const mlClassicMargin = userPrice > 0 ? (mlClassicProfit / userPrice) * 100 : 0;

    // Mercado Livre Premium (16.5% + R$6.25)
    const mlPremiumFeePercent = 16.5;
    const mlPremiumFixedCost = 6.25;
    const mlPremiumListingPrice = (userPrice + mlPremiumFixedCost) / (1 - mlPremiumFeePercent / 100);
    const mlPremiumFee = (mlPremiumListingPrice * mlPremiumFeePercent) / 100;
    const mlPremiumProfit = userPrice - cost;
    const mlPremiumMargin = userPrice > 0 ? (mlPremiumProfit / userPrice) * 100 : 0;

    // Shopee (14% + R$3)
    const shopeeFeePercent = 14;
    const shopeeFixedFee = 3;
    const shopeeListingPrice = (userPrice + shopeeFixedFee) / (1 - shopeeFeePercent / 100);
    const shopeeTotalFees = (shopeeListingPrice * shopeeFeePercent) / 100 + shopeeFixedFee;
    const shopeeProfit = userPrice - cost;
    const shopeeMargin = userPrice > 0 ? (shopeeProfit / userPrice) * 100 : 0;

    setCalculations({
      mercadolivreClassic: {
        suggestedPrice: mlClassicListingPrice,
        profit: mlClassicProfit,
        fee: mlClassicFee,
        fixedCost: mlClassicFixedCost,
        margin: mlClassicMargin
      },
      mercadolivrePremium: {
        suggestedPrice: mlPremiumListingPrice,
        profit: mlPremiumProfit,
        fee: mlPremiumFee,
        fixedCost: mlPremiumFixedCost,
        margin: mlPremiumMargin
      },
      shopee: {
        suggestedPrice: shopeeListingPrice,
        profit: shopeeProfit,
        totalFees: shopeeTotalFees,
        margin: shopeeMargin
      }
    });
  };

  const getMarginColor = (margin) => {
    if (margin < 0) return 'text-red-600';
    if (margin < 15) return 'text-orange-600';
    if (margin >= 15 && margin <= 35) return 'text-green-600';
    return 'text-blue-600';
  };

  const getMarginInsight = (margin) => {
    if (margin < 0) {
      return {
        text: 'Atenção! Preço abaixo do custo. Você terá prejuízo.',
        color: 'text-red-600',
        bgColor: 'bg-red-900/20',
        borderColor: 'border-red-600/30'
      };
    } else if (margin < 15) {
      return {
        text: 'Margem baixa. Considere aumentar o preço para melhorar a rentabilidade.',
        color: 'text-orange-600',
        bgColor: 'bg-orange-900/20',
        borderColor: 'border-orange-600/30'
      };
    } else if (margin >= 15 && margin <= 35) {
      return {
        text: 'Margem saudável. Preço competitivo com boa rentabilidade.',
        color: 'text-green-600',
        bgColor: 'bg-green-900/20',
        borderColor: 'border-green-600/30'
      };
    } else {
      return {
        text: 'Margem alta. Verifique a competitividade do preço no mercado.',
        color: 'text-blue-600',
        bgColor: 'bg-blue-900/20',
        borderColor: 'border-blue-600/30'
      };
    }
  };

  const currentMargin = calculations?.mercadolivreClassic?.margin || 0;
  const insight = getMarginInsight(currentMargin);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* LEFT SIDE - PRICING INPUT */}
      <div className="space-y-6">
        {/* Pricing Input Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Precificação</h3>
              <p className="text-sm text-gray-500">Defina o preço de venda do produto</p>
            </div>
          </div>

          {/* Cost Input (READ-ONLY from CSV) */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              CUSTO DO PRODUTO
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
              <input
                type="number"
                step="0.01"
                value={productData?.cost || ''}
                onChange={(e) => onFieldChange('cost', parseFloat(e?.target?.value) || 0)}
                className="w-full pl-12 pr-4 py-3 text-2xl font-bold text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Custo do produto (editável)
            </p>
          </div>

          {/* Price Input (MANUAL USER INPUT) */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              PREÇO DE VENDA (R$) *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
              <input
                type="number"
                step="0.01"
                value={productData?.price || ''}
                onChange={(e) => onFieldChange('price', e?.target?.value)}
                className={`w-full pl-12 pr-4 py-3 text-2xl font-bold text-gray-900 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors?.price ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
            </div>
            {validationErrors?.price && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {validationErrors?.price}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Este é o valor que você deseja receber após as taxas do marketplace
            </p>
          </div>

          {/* Margin Display */}
          {productData?.cost > 0 && productData?.price > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-900">Margem de Lucro</span>
                <span className={`text-2xl font-bold ${getMarginColor(currentMargin)}`}>
                  {currentMargin?.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Lucro Líquido</span>
                <span className={`font-bold ${getMarginColor(currentMargin)}`}>
                  R$ {calculations?.mercadolivreClassic?.profit?.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Market Insights Panel */}
        {productData?.cost > 0 && productData?.price > 0 && (
          <div className={`${insight?.bgColor} border ${insight?.borderColor} rounded-xl p-5`}>
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Icon name="TrendingUp" size={20} className={insight?.color} />
              </div>
              <div>
                <h4 className={`text-sm font-bold ${insight?.color} mb-1`}>INSIGHT DE PRECIFICAÇÃO</h4>
                <p className={`text-sm ${insight?.color}`}>
                  {insight?.text}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT SIDE - MARKETPLACE PRICING */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Preços nos Marketplaces</h3>
          <div className="flex items-center gap-2 text-xs text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Calculado automaticamente
          </div>
        </div>

        {productData?.cost > 0 && productData?.price > 0 ? (
          <>
            {/* Mercado Livre Classic */}
            <div className="bg-yellow-400 rounded-xl p-5 shadow-md relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                  Líder de Vendas
                </span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Icon name="ShoppingBag" size={20} className="text-yellow-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900">Mercado Livre</h4>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Classic */}
                <div className="bg-white/90 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-600 mb-1">CLÁSSICO</p>
                  <p className="text-sm text-blue-600 font-semibold mb-2">{11.5}%</p>
                  <p className="text-3xl font-bold text-gray-900 mb-1">
                    R$ {calculations?.mercadolivreClassic?.suggestedPrice?.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Preço de listagem</p>
                </div>

                {/* Premium */}
                <div className="bg-white/90 rounded-lg p-4">
                  <p className="text-xs font-semibold text-cyan-600 mb-1">PREMIUM</p>
                  <p className="text-sm text-cyan-600 font-semibold mb-2">{16.5}%</p>
                  <p className="text-3xl font-bold text-gray-900 mb-1">
                    R$ {calculations?.mercadolivrePremium?.suggestedPrice?.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Preço de listagem</p>
                </div>
              </div>

              {/* Fee Breakdown - Classic */}
              <div className="bg-white/70 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Preço de Listagem</span>
                  <span className="font-semibold text-gray-900">R$ {calculations?.mercadolivreClassic?.suggestedPrice?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tarifa {11.5}%</span>
                  <span className="text-red-600 font-semibold">-R$ {calculations?.mercadolivreClassic?.fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Custo Fixo</span>
                  <span className="text-red-600 font-semibold">-R$ {calculations?.mercadolivreClassic?.fixedCost?.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 flex justify-between items-center">
                  <span className="font-bold text-gray-900">Você Recebe</span>
                  <span className="text-xl font-bold text-green-600">
                    R$ {productData?.price}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Lucro Líquido</span>
                  <span className={`font-bold ${getMarginColor(calculations?.mercadolivreClassic?.margin)}`}>
                    R$ {calculations?.mercadolivreClassic?.profit?.toFixed(2)} ({calculations?.mercadolivreClassic?.margin?.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Shopee */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-5 shadow-md relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <span className="px-3 py-1 bg-white text-orange-600 text-xs font-bold rounded-full">
                  Foco em Volume
                </span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Icon name="ShoppingCart" size={20} className="text-orange-600" />
                </div>
                <h4 className="text-xl font-bold text-white">Shopee</h4>
              </div>

              <div className="bg-white/95 rounded-lg p-4 mb-3">
                <p className="text-xs font-semibold text-gray-600 mb-1">PREÇO DE LISTAGEM</p>
                <p className="text-4xl font-bold text-orange-600 mb-2">
                  R$ {calculations?.shopee?.suggestedPrice?.toFixed(2)}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Taxas ({14}% + R${3})</span>
                    <span className="text-red-600 font-semibold">-R$ {calculations?.shopee?.totalFees?.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between">
                    <span className="font-bold text-gray-900">Você Recebe</span>
                    <span className="text-xl font-bold text-green-600">R$ {productData?.price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lucro Líquido</span>
                    <span className={`font-bold ${getMarginColor(calculations?.shopee?.margin)}`}>
                      R$ {calculations?.shopee?.profit?.toFixed(2)} ({calculations?.shopee?.margin?.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
            <Icon name="Calculator" size={48} className="text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold mb-1">Preencha os campos de precificação</p>
            <p className="text-sm text-gray-500">Defina o custo e o preço de venda para ver os cálculos dos marketplaces</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingTab;